import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Signal,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { MapInitializer } from '../../application/map-initializer';
import { filter, switchMap, take, tap } from 'rxjs';
import { LocationButtonComponent } from '@ui-components';
import { NgClass } from '@angular/common';
import {
  AnalyticsService,
  AUTH_CLIENT_TOKEN,
  AuthClientProvider,
  FindGamesAnalyticsEvent,
  GameDetailsModel,
  GlobalRefreshService,
  LocationAccessService,
  LocationAnalyticsData,
  MODAL_TOKEN,
  ModalProvider,
  PLATFORM_TOKEN,
  PlatformProvider
} from '@core';
import {
  GetMapMarkersQueryHandler,
  GetMapMarkersQueryResult,
  MapMarkerLocationModel
} from '../../application/handlers';
import { IonicModule } from '@ionic/angular';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FiltersComponent } from '../filters/filters.component';

@Component({
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  providers: [GetMapMarkersQueryHandler],
  imports: [NgClass, LocationButtonComponent, IonicModule, FiltersComponent]
})
export class MapComponent implements AfterViewInit {
  @ViewChild('map') map: ElementRef = {} as ElementRef;

  private readonly mapInitializer: MapInitializer = inject(MapInitializer);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly getMapMarkersQueryHandler: GetMapMarkersQueryHandler = inject(GetMapMarkersQueryHandler);
  private readonly globalRefreshService: GlobalRefreshService = inject(GlobalRefreshService);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly authClientProvider: AuthClientProvider = inject(AUTH_CLIENT_TOKEN);
  private readonly locationAccessService: LocationAccessService = inject(LocationAccessService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);

  private readonly markerIconCounter: string = 'assets/icons/map/map-marker-counter.svg';
  private readonly markerIconShade: string = 'assets/icons/map/map-marker-shade.svg';
  private readonly markerIconShadeCounter: string = 'assets/icons/map/map-marker-shade-counter.svg';

  readonly isMapLoaded: Signal<{ isLoaded: boolean } | undefined> = toSignal(this.mapInitializer.isMapLoaded$);

  readonly isAuthed: Signal<boolean | undefined> = toSignal(this.authClientProvider.isAuthed$);

  readonly isiOS: boolean = this.platformProvider.isiOS;

  readonly isNativeRun: boolean = this.platformProvider.isNativeRun;

  ngAfterViewInit(): void {
    this.modalProvider
      .showLoading$()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() =>
          this.mapInitializer.initMap({
            mapContainerName: this.map?.nativeElement
          })
        )
      )
      .subscribe();

    this.mapInitializer.isMapLoaded$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((isLoaded: { isLoaded: boolean }) => isLoaded.isLoaded),
        switchMap(() => this.mapInitializer.setUserPosition(!this.mapInitializer.hasRestoredView, true)),
        tap(() => this.modalProvider.dismissLoading$())
      )
      .subscribe();

    this.globalRefreshService.refresh$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => this.mapInitializer.setUserPosition(true, false))
      )
      .subscribe();

    this.initMarkers();
  }

  onLocationBannerClicked(): void {
    this.locationAccessService
      .accessState$()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((state: LocationAnalyticsData) =>
        this.analyticsService.buttonClick(FindGamesAnalyticsEvent.MapBannerLocation, state)
      );
  }

  private initMarkers(): void {
    this.getMapMarkersQueryHandler
      .getMapMarkers()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.mapInitializer.clearMarkers()),
        tap((markers: GetMapMarkersQueryResult) =>
          markers.locations.map((location: MapMarkerLocationModel) =>
            this.mapInitializer.addMarker<GameDetailsModel[]>({
              coordinates: location.coordinates,
              showCounter: true,
              counterValue: location.games.length > 1 ? location.games.length.toString() : undefined,
              games: location.games,
              locationId: location.locationId,
              markerIcon: this.setMarkerIcon(location),
              isUserLocation: false
            })
          )
        )
      )
      .subscribe();
  }

  private setMarkerIcon(location: MapMarkerLocationModel): string | undefined {
    if (!location.hasRelationWithSomeGame) {
      if (location.games.length > 1) {
        return this.markerIconCounter;
      }
      return undefined;
    }

    if (location.games.length > 1) {
      return this.markerIconShadeCounter;
    }

    return this.markerIconShade;
  }
}
